import {
  initTracerError,
  initTracerErrorAsyncStack,
  initTracerErrorUploader,
  initTracerSessionUploader,
  ITracerUploaderConfig,
} from '@apptracer/sdk'
import { validateGuid } from '@builddocs/data-management'
import { openDB, DBSchema } from 'idb'

/*
 * AppTracer
 * */

const config: ITracerUploaderConfig = {
  versionName: `builddocs-js-${process.env.NODE_ENV || process.env.NX_ENV}`,
  versionCode: 1,
  appToken: process.env.NX_APPTRACER_API_TOKEN!,
  environment: process.env.NODE_ENV || process.env.NX_ENV,
}

const tracerError = initTracerError({
  handleOtherErrorDataType: true,
  stringifyErrorDataObject: true,
})

initTracerErrorUploader(config)
initTracerSessionUploader(config)
initTracerErrorAsyncStack()

/*
 * idb
 * */

const StoreNames = {
  selectedWorkDocSections: 'selectedWorkDocSections',
  savedWorkingDrafts: 'savedWorkingDrafts',
} as const

interface BdSchema extends DBSchema {
  [StoreNames.selectedWorkDocSections]: {
    key: string
    value: { workDocSection: string; stageId: number } | undefined
  }
}

const dbPromise = openDB<BdSchema>('builddocs-idb', 1, {
  upgrade(db, _oldVersion, _newVersion, transaction, _event) {
    if (!db.objectStoreNames.contains(StoreNames.selectedWorkDocSections)) {
      db.createObjectStore(StoreNames.selectedWorkDocSections)
    }

    const uuidKeys = Object.keys(localStorage).filter(key => validateGuid(key))
    const uuidKeyValues = uuidKeys.map(key => ({
      value: localStorage.getItem(key),
      key,
    }))

    const workDocSections = uuidKeyValues
      .filter(record => {
        if (!record.value) return false
        try {
          if (record.value === 'undefined') {
            return true
          }
          JSON.parse(record.value)
          return true
        } catch (e) {
          return false
        }
      })
      .map(workDocSection => ({
        key: workDocSection.key,
        value:
          workDocSection.value === 'undefined'
            ? undefined
            : JSON.parse(workDocSection.value!),
      }))

    const promises = workDocSections
      .filter(record => Boolean(record.value))
      .map(record =>
        transaction.objectStore('selectedWorkDocSections').put(
          {
            workDocSection: record.value.workDocSection,
            stageId: record.value.stageId,
          },
          record.key,
        ),
      )

    Promise.all([...promises, transaction.done]).then(() => {
      workDocSections.forEach(record => localStorage.removeItem(record.key))
    })
  },
  // blocked(currentVersion, blockedVersion, event) {
  //   // …
  // },
  // blocking(currentVersion, blockedVersion, event) {
  //   // …
  // },
  // terminated() {
  //   // …
  // },
})

dbPromise.catch(err => {
  tracerError.error(err, {
    severity: 'error',
    issueKey: 'idb migration issue',
  })
})

const add = async (
  key: string,
  value: { workDocSection: string; stageId: number } | undefined,
) => {
  const db = await dbPromise

  await db.add(StoreNames.selectedWorkDocSections, value, key)
}

const put = async (
  key: string,
  value: { workDocSection: string; stageId: number } | undefined,
) => {
  const db = await dbPromise

  return db.put(StoreNames.selectedWorkDocSections, value, key)
}

const get = async (key: string) => {
  const db = await dbPromise

  return db.get(StoreNames.selectedWorkDocSections, key)
}

const del = async (key: string) => {
  const db = await dbPromise

  await db.delete(StoreNames.selectedWorkDocSections, key)
}

export { dbPromise, StoreNames, add, get, del, put, tracerError }
